import React from "react";
import {
  CheckCircle2,
  Clock3,
  Home,
  Loader2,
  LockKeyhole,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  WalletCards,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "react-router-dom";
import StudentLayout from "../../components/layouts/StudentLayout";
import { useAuth } from "../../contexts/AuthContext";
import { getActiveEnrollmentContext } from "../../services/enrollmentService";
import { studentService } from "../../services/studentService";
import hostelService from "../../services/hostelService";

const money = (value) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
const formatDateOnly = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", { dateStyle: "medium" }).format(
        new Date(`${value}T00:00:00`),
      )
    : "—";
const statusTone = (status) =>
  ({
    PENDING_PAYMENT: "bg-amber-50 text-amber-800 border-amber-200",
    ALLOCATED: "bg-emerald-50 text-emerald-800 border-emerald-200",
    OCCUPIED: "bg-blue-50 text-blue-800 border-blue-200",
    PAYMENT_REVIEW: "bg-rose-50 text-rose-800 border-rose-200",
  })[status] || "bg-slate-100 text-slate-700 border-slate-200";

const HostelReservationPortal = ({ embedded = false }) => {
  const { user, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const requestView = searchParams.get("view") === "requests";
  const [student, setStudent] = React.useState(null);
  const [period, setPeriod] = React.useState(null);
  const [bookingWindow, setBookingWindow] = React.useState({
    configured: false,
    booking_enabled: false,
    opens_on: "",
    closes_on: "",
    status: "unconfigured",
    can_book: false,
  });
  const [beds, setBeds] = React.useState([]);
  const [roomChangeBeds, setRoomChangeBeds] = React.useState([]);
  const [roomChangeLoading, setRoomChangeLoading] = React.useState(false);
  const [reservations, setReservations] = React.useState([]);
  const [requests, setRequests] = React.useState([]);
  const [excessCredits, setExcessCredits] = React.useState([]);
  const [requestForm, setRequestForm] = React.useState({
    requestType: "maintenance",
    subject: "",
    description: "",
    priority: "normal",
    targetBedId: "",
  });
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [message, setMessage] = React.useState(null);
  const [now, setNow] = React.useState(Date.now());
  const [filters, setFilters] = React.useState({
    search: "",
    block: "all",
    category: "all",
    roomType: "all",
    maxAmount: "",
  });

  const notify = (text, type = "success") => {
    setMessage({ text, type });
    window.setTimeout(() => setMessage(null), 4500);
  };
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [studentRecord, context] = await Promise.all([
        studentService.getStudentById(user?.id),
        getActiveEnrollmentContext(),
      ]);
      setStudent(studentRecord);
      if (
        !context.success ||
        !context.data?.academicYear ||
        !context.data?.semester
      ) {
        setPeriod(null);
        setBookingWindow({
          configured: false,
          booking_enabled: false,
          opens_on: "",
          closes_on: "",
          status: "unconfigured",
          can_book: false,
        });
        setBeds([]);
        const [mine, myRequests, credits] = await Promise.all([
          hostelService.getMyReservations(),
          hostelService.getMyRequests(),
          hostelService.getMyExcessCredits(),
        ]);
        setReservations(mine);
        setRequests(myRequests);
        setExcessCredits(credits || []);
        return;
      }
      setPeriod(context.data);
      const availableBeds = requestView
        ? Promise.resolve([])
        : hostelService.searchAvailableBeds(
            context.data.academicYear.id,
            context.data.semester.id,
          );
      const [available, mine, myRequests, credits, window] = await Promise.all([
        availableBeds,
        hostelService.getMyReservations(),
        hostelService.getMyRequests(),
        hostelService.getMyExcessCredits(),
        hostelService.getBookingWindow(
          context.data.academicYear.id,
          context.data.semester.id,
        ),
      ]);
      setBeds(available || []);
      setReservations(mine || []);
      setRequests(myRequests || []);
      setExcessCredits(credits || []);
      setBookingWindow(
        window || {
          configured: false,
          booking_enabled: false,
          opens_on: "",
          closes_on: "",
          status: "unconfigured",
          can_book: false,
        },
      );
    } catch (error) {
      notify(
        error?.message || "Accommodation data could not be loaded",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [requestView, user?.id]);
  React.useEffect(() => {
    if (user?.id) load();
  }, [load, user?.id]);
  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const reserve = async (bed) => {
    if (!period) return;
    if (!bookingWindow.can_book) {
      notify(
        "Hostel booking is closed for the selected academic period.",
        "error",
      );
      return;
    }
    setBusy(bed.bed_id);
    try {
      const result = await hostelService.reserveBed(
        bed.bed_id,
        period.academicYear.id,
        period.semester.id,
      );
      notify(
        `Bed ${bed.bed_number} held for 30 minutes. Complete payment before the timer expires.`,
      );
      setReservations((previous) => [
        {
          ...(result?.reservation || {}),
          block_name: bed.block_name,
          room_number: bed.room_number,
          bed_number: bed.bed_number,
          invoice: result?.invoice,
        },
        ...previous,
      ]);
      setBeds((previous) =>
        previous.filter((item) => item.bed_id !== bed.bed_id),
      );
    } catch (error) {
      notify(error?.message || "This bed is no longer available", "error");
      await load();
    } finally {
      setBusy("");
    }
  };
  const confirmPayment = async (reservation) => {
    setBusy(`payment-${reservation.id}`);
    try {
      await hostelService.confirmPayment(
        reservation.id,
        `PORTAL-${reservation.id}`,
      );
      notify(
        "Payment verified. Your bed is now allocated and your digital pass is ready.",
      );
      await load();
    } catch (error) {
      notify(error?.message || "Payment could not be verified", "error");
    } finally {
      setBusy("");
    }
  };
  const confirmRoomChangePayment = async (request) => {
    setBusy(`room-change-payment-${request.id}`);
    try {
      await hostelService.confirmRoomChangePayment(
        request.id,
        `PORTAL-ROOM-CHANGE-${request.id}`,
      );
      notify(
        "Room-change payment verified. Your request has been sent for hostel office approval.",
      );
      setRequests(await hostelService.getMyRequests());
    } catch (error) {
      notify(
        error?.message || "Room-change payment could not be verified",
        "error",
      );
    } finally {
      setBusy("");
    }
  };

  const requestCreditClaim = async (credit) => {
    setBusy(`credit-claim-${credit.id}`);
    try {
      await hostelService.requestExcessCreditClaim(credit.id, "cash_refund");
      notify(
        "Your accommodation credit claim was sent to the hostel office for processing.",
      );
      setExcessCredits(await hostelService.getMyExcessCredits());
    } catch (error) {
      notify(
        error?.message || "The credit claim could not be submitted",
        "error",
      );
    } finally {
      setBusy("");
    }
  };
  const applyCreditToReservation = async (reservation) => {
    const credit = excessCredits.find(
      (item) =>
        item.status === "AVAILABLE" &&
        ["APPROVED", "RESOLVED"].includes(item.request_status) &&
        Number(item.remaining_amount) > 0,
    );
    const invoiceId = reservation.invoice?.id;
    if (!credit || !invoiceId) return;
    setBusy(`credit-apply-${reservation.id}`);
    try {
      const result = await hostelService.applyExcessCredit(
        credit.id,
        invoiceId,
        Math.min(
          Number(credit.remaining_amount || 0),
          Number(reservation.invoice?.amount || reservation.amount || 0),
        ),
      );
      notify(
        `${money(result?.applied_amount)} accommodation credit applied. Remaining payment: ${money(result?.payable_amount)}.`,
      );
      await load();
    } catch (error) {
      notify(
        error?.message || "The accommodation credit could not be applied",
        "error",
      );
    } finally {
      setBusy("");
    }
  };
  const cancel = async (reservation) => {
    setBusy(`cancel-${reservation.id}`);
    try {
      await hostelService.cancelReservation(reservation.id);
      notify("Reservation released back to the accommodation pool.");
      await load();
    } catch (error) {
      notify(error?.message || "Reservation could not be cancelled", "error");
    } finally {
      setBusy("");
    }
  };
  const submitRequest = async (event) => {
    event.preventDefault();
    setBusy("request");
    try {
      if (requestForm.requestType === "room_change") {
        if (!activeReservation?.id || !requestForm.targetBedId) {
          throw new Error("Select an available target bed for the room change");
        }
        await hostelService.createRoomChangeRequest({
          subject: requestForm.subject,
          description: requestForm.description,
          priority: requestForm.priority,
          currentReservationId: activeReservation.id,
          targetBedId: requestForm.targetBedId,
        });
      } else {
        await hostelService.createRequest({
          ...requestForm,
          reservationId: activeReservation?.id || null,
        });
      }
      setRequestForm({
        requestType: "maintenance",
        subject: "",
        description: "",
        priority: "normal",
        targetBedId: "",
      });
      notify(
        "Accommodation request submitted. Hostel staff will update its status.",
      );
      setRequests(await hostelService.getMyRequests());
    } catch (error) {
      notify(error?.message || "Request could not be submitted", "error");
    } finally {
      setBusy("");
    }
  };
  const fullName =
    [student?.first_name, student?.middle_name, student?.last_name]
      .filter(Boolean)
      .join(" ") ||
    profile?.full_name ||
    "Student";
  const activeReservation = reservations.find(
    (item) =>
      Boolean(period?.academicYear?.id && period?.semester?.id) &&
      String(item.academic_year_id) === String(period?.academicYear?.id) &&
      String(item.semester_id) === String(period?.semester?.id) &&
      [
        "PENDING_PAYMENT",
        "HELD_RESERVED",
        "ALLOCATED",
        "OCCUPIED",
        "PAYMENT_REVIEW",
      ].includes(item.status),
  );
  const secondsLeft = activeReservation?.hold_expires_at
    ? Math.max(
        0,
        Math.floor(
          (new Date(activeReservation.hold_expires_at).getTime() - now) / 1000,
        ),
      )
    : null;

  React.useEffect(() => {
    const academicYearId = period?.academicYear?.id;
    const semesterId = period?.semester?.id;
    if (
      requestForm.requestType !== "room_change" ||
      !academicYearId ||
      !semesterId ||
      !activeReservation?.id
    ) {
      setRoomChangeBeds([]);
      setRoomChangeLoading(false);
      return undefined;
    }

    let cancelled = false;
    setRoomChangeLoading(true);
    hostelService
      .searchRoomChangeBeds(academicYearId, semesterId, activeReservation.id)
      .then((available) => {
        if (!cancelled) setRoomChangeBeds(available || []);
      })
      .catch((error) => {
        if (!cancelled) {
          setRoomChangeBeds([]);
          notify(
            error?.message || "Available transfer beds could not be loaded",
            "error",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setRoomChangeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeReservation?.id,
    period?.academicYear?.id,
    period?.semester?.id,
    requestForm.requestType,
  ]);

  const selectedRoomChangeBed = roomChangeBeds.find(
    (bed) => bed.bed_id === requestForm.targetBedId,
  );
  const currentPaidAmount = Number(
    activeReservation?.invoice?.amount || activeReservation?.amount || 0,
  );
  const roomChangeDifference = Math.max(
    Number(selectedRoomChangeBed?.amount || 0) - currentPaidAmount,
    0,
  );

  // Release an unpaid hold as soon as its countdown ends while the portal is
  // open. The database RPC is idempotent and also cancels the linked invoice,
  // so a background tab or a second user cannot retain the bed indefinitely.
  React.useEffect(() => {
    if (
      !activeReservation ||
      !["PENDING_PAYMENT", "HELD_RESERVED"].includes(activeReservation.status)
    ) {
      return undefined;
    }

    const expiresAt = new Date(activeReservation.hold_expires_at).getTime();
    if (!Number.isFinite(expiresAt)) return undefined;

    let cancelled = false;
    const release = async () => {
      try {
        await hostelService.releaseExpired();
        if (!cancelled) await load();
      } catch (error) {
        console.warn(
          "Expired accommodation hold could not be released yet:",
          error?.message || error,
        );
      }
    };

    const timer = window.setTimeout(
      release,
      Math.max(0, expiresAt - Date.now() + 250),
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    activeReservation?.id,
    activeReservation?.hold_expires_at,
    activeReservation?.status,
    load,
  ]);

  const blockOptions = React.useMemo(
    () =>
      Array.from(
        new Map(
          beds.map((bed) => [
            bed.block_id,
            { id: bed.block_id, name: bed.block_name },
          ]),
        ).values(),
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [beds],
  );
  const categoryOptions = React.useMemo(
    () =>
      Array.from(
        new Set(beds.map((bed) => bed.hostel_category).filter(Boolean)),
      ).sort(),
    [beds],
  );
  const roomTypeOptions = React.useMemo(
    () =>
      Array.from(
        new Set(beds.map((bed) => bed.room_type).filter(Boolean)),
      ).sort(),
    [beds],
  );
  const filteredBeds = React.useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return beds.filter((bed) => {
      const haystack = [
        bed.block_name,
        bed.block_code,
        bed.hostel_category,
        bed.room_number,
        bed.bed_number,
        ...(bed.amenities || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (search && !haystack.includes(search)) return false;
      if (filters.block !== "all" && bed.block_id !== filters.block)
        return false;
      if (
        filters.category !== "all" &&
        bed.hostel_category !== filters.category
      )
        return false;
      if (filters.roomType !== "all" && bed.room_type !== filters.roomType)
        return false;
      if (
        filters.maxAmount &&
        Number(bed.amount || 0) > Number(filters.maxAmount)
      )
        return false;
      return true;
    });
  }, [beds, filters]);
  const resetFilters = () =>
    setFilters({
      search: "",
      block: "all",
      category: "all",
      roomType: "all",
      maxAmount: "",
    });

  return (
    <StudentLayout student={student} embedded={embedded}>
      <section className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-blue-700">Accommodation</p>
            <h1 className="mt-1 text-3xl font-bold text-slate-950">
              {requestView
                ? "Accommodation requests"
                : "Hostel room reservation"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {requestView
                ? "Submit and track room changes, maintenance issues and other accommodation requests."
                : "Select one available bed for the active academic period. A bed is held atomically while payment is completed."}
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          >
            {!requestView && (
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            )}
            Refresh
          </button>
        </div>
        {message && (
          <div
            className={`rounded-lg border p-4 text-sm ${message.type === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
          >
            {message.text}
          </div>
        )}
        {!period && !loading && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div>
              <p className="font-semibold">No active accommodation period</p>
              <p className="mt-1">
                The institution has not opened an active academic year and
                semester for room reservations.
              </p>
            </div>
          </div>
        )}
        {!requestView && period && !loading && !bookingWindow.can_book && (
          <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
            <div>
              <p className="font-semibold">
                Hostel booking is {bookingWindow.status || "closed"}
              </p>
              <p className="mt-1">
                {bookingWindow.opens_on && bookingWindow.closes_on
                  ? `Booking window: ${formatDateOnly(bookingWindow.opens_on)} – ${formatDateOnly(bookingWindow.closes_on)}.`
                  : "The hostel office has not configured booking dates for this academic period."}
              </p>
            </div>
          </div>
        )}
        {!requestView && activeReservation && (
          <section className="rounded-xl border bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <LockKeyhole className="h-5 w-5 text-blue-700" />
                  <h2 className="text-lg font-bold">Your reservation</h2>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {activeReservation.block_name} · Room{" "}
                  {activeReservation.room_number} · Bed{" "}
                  {activeReservation.bed_number}
                </p>
              </div>
              <span
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(activeReservation.status)}`}
              >
                {activeReservation.status.replaceAll("_", " ")}
              </span>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Invoice
                </p>
                <p className="mt-1 font-mono text-sm">
                  {activeReservation.invoice?.invoice_number || "—"}
                </p>
                <p className="text-sm font-semibold">
                  {money(
                    activeReservation.invoice?.amount ||
                      activeReservation.amount,
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Hold expires
                </p>
                <p className="mt-1 text-sm font-semibold">
                  {formatDate(activeReservation.hold_expires_at)}
                </p>
                {secondsLeft !== null &&
                  activeReservation.status === "PENDING_PAYMENT" && (
                    <p className="text-sm font-bold text-amber-700">
                      <Clock3 className="mr-1 inline h-4 w-4" />
                      {Math.floor(secondsLeft / 60)}:
                      {String(secondsLeft % 60).padStart(2, "0")} remaining
                    </p>
                  )}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                {["PENDING_PAYMENT", "HELD_RESERVED"].includes(
                  activeReservation.status,
                ) && (
                  <button
                    disabled={busy === `payment-${activeReservation.id}`}
                    onClick={() => confirmPayment(activeReservation)}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    <WalletCards className="h-4 w-4" />
                    {busy === `payment-${activeReservation.id}`
                      ? "Verifying…"
                      : "Verify payment"}
                  </button>
                )}
                {["PENDING_PAYMENT", "HELD_RESERVED"].includes(
                  activeReservation.status,
                ) && (
                  <button
                    disabled={busy === `cancel-${activeReservation.id}`}
                    onClick={() => cancel(activeReservation)}
                    className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
            {activeReservation.status === "PAYMENT_REVIEW" && (
              <p className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
                Payment was received after the hold was taken. Finance/hostel
                staff will review it and credit the payment if another bed must
                be selected.
              </p>
            )}
            {activeReservation.status === "PENDING_PAYMENT" &&
              activeReservation.invoice?.id &&
              excessCredits.some(
                (credit) =>
                  credit.status === "AVAILABLE" &&
                  ["APPROVED", "RESOLVED"].includes(credit.request_status) &&
                  Number(credit.remaining_amount) > 0,
              ) && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <p>
                    You have an accommodation credit available. Apply it to this
                    booking before completing payment.
                  </p>
                  <button
                    type="button"
                    disabled={busy === `credit-apply-${activeReservation.id}`}
                    onClick={() => applyCreditToReservation(activeReservation)}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                  >
                    <WalletCards className="h-4 w-4" />
                    {busy === `credit-apply-${activeReservation.id}`
                      ? "Applying…"
                      : "Apply accommodation credit"}
                  </button>
                </div>
              )}
            {activeReservation.pass && (
              <div className="mt-5 flex flex-wrap items-center gap-5 border-t pt-5">
                <QRCodeSVG
                  value={
                    activeReservation.pass.qr_payload ||
                    activeReservation.pass.token
                  }
                  size={112}
                  includeMargin
                />
                <div>
                  <p className="flex items-center gap-2 font-semibold text-emerald-700">
                    <CheckCircle2 className="h-5 w-5" />
                    Digital check-in pass issued
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Show this QR code to the warden for clearance and key
                    collection.
                  </p>
                  <p className="mt-2 font-mono text-xs text-slate-600">
                    {activeReservation.pass.token}
                  </p>
                </div>
              </div>
            )}
          </section>
        )}
        {!requestView && (
          <section className="rounded-xl border bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">Available beds</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {period
                    ? `${period.academicYear.name} · ${period.semester.name}`
                    : "Active period required"}
                </p>
              </div>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                {filteredBeds.length} of {beds.length} available
              </span>
            </div>
            {activeReservation &&
              ["ALLOCATED", "OCCUPIED"].includes(activeReservation.status) && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    Your bed is allocated for this academic period. You can view
                    the available beds, but you cannot reserve another bed until
                    the next semester.
                  </p>
                </div>
              )}
            <div className="mt-4 grid gap-3 rounded-xl border bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-5">
              <label className="flex flex-col gap-1 lg:col-span-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Search
                </span>
                <span className="flex items-center rounded-lg border bg-white px-3">
                  <Search className="mr-2 h-4 w-4 text-slate-400" />
                  <input
                    value={filters.search}
                    onChange={(event) =>
                      setFilters({ ...filters, search: event.target.value })
                    }
                    placeholder="Hostel, room, bed or amenity"
                    className="min-w-0 flex-1 py-2 text-sm outline-none"
                  />
                </span>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Hostel
                </span>
                <select
                  value={filters.block}
                  onChange={(event) =>
                    setFilters({ ...filters, block: event.target.value })
                  }
                  className="rounded-lg border bg-white px-3 py-2 text-sm"
                >
                  <option value="all">All hostels</option>
                  {blockOptions.map((block) => (
                    <option key={block.id} value={block.id}>
                      {block.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Type
                </span>
                <select
                  value={filters.category}
                  onChange={(event) =>
                    setFilters({ ...filters, category: event.target.value })
                  }
                  className="rounded-lg border bg-white px-3 py-2 text-sm capitalize"
                >
                  <option value="all">All types</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Room type
                </span>
                <select
                  value={filters.roomType}
                  onChange={(event) =>
                    setFilters({ ...filters, roomType: event.target.value })
                  }
                  className="rounded-lg border bg-white px-3 py-2 text-sm capitalize"
                >
                  <option value="all">All room types</option>
                  {roomTypeOptions.map((roomType) => (
                    <option key={roomType} value={roomType}>
                      {roomType}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Maximum total
                </span>
                <input
                  type="number"
                  min="0"
                  value={filters.maxAmount}
                  onChange={(event) =>
                    setFilters({ ...filters, maxAmount: event.target.value })
                  }
                  placeholder="KES"
                  className="rounded-lg border bg-white px-3 py-2 text-sm"
                />
              </label>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center justify-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm font-semibold text-slate-700 lg:self-end"
              >
                <SlidersHorizontal className="h-4 w-4" /> Clear filters
              </button>
            </div>
            {loading ? (
              <div className="grid min-h-40 place-items-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : beds.length === 0 ? (
              <div className="mt-5 rounded-lg border border-dashed p-10 text-center">
                <Home className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm font-semibold text-slate-700">
                  No eligible beds available
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Beds are filtered by gender eligibility and existing
                  reservations.
                </p>
              </div>
            ) : filteredBeds.length === 0 ? (
              <div className="mt-5 rounded-lg border border-dashed p-10 text-center">
                <Search className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm font-semibold text-slate-700">
                  No beds match these filters
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-3 rounded-lg border px-3 py-2 text-sm font-semibold text-slate-700"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="mt-5 overflow-x-auto rounded-xl border">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Hostel</th>
                      <th className="px-4 py-3">Room / bed</th>
                      <th className="px-4 py-3">Room type</th>
                      <th className="px-4 py-3">Eligibility</th>
                      <th className="px-4 py-3">Amenities</th>
                      <th className="px-4 py-3">Total</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBeds.map((bed) => (
                      <tr
                        key={bed.bed_id}
                        className="align-top hover:bg-slate-50/70"
                      >
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">
                            {bed.block_name}
                          </p>
                          <p className="mt-1 text-xs uppercase text-indigo-700">
                            {bed.hostel_category || "standard"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {bed.block_code}
                          </p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <p className="font-semibold">
                            Room {bed.room_number}
                          </p>
                          <p className="text-xs text-slate-500">
                            Bed {bed.bed_number} · {bed.capacity} beds
                          </p>
                        </td>
                        <td className="px-4 py-3 capitalize">
                          {bed.room_type}
                        </td>
                        <td className="px-4 py-3 capitalize">
                          {bed.gender_eligibility}
                        </td>
                        <td className="max-w-[220px] px-4 py-3 text-xs text-slate-500">
                          {bed.amenities?.length
                            ? bed.amenities.join(", ")
                            : "No amenities listed"}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-900">
                          {money(bed.amount)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            disabled={
                              Boolean(activeReservation) ||
                              !bookingWindow.can_book ||
                              busy === bed.bed_id
                            }
                            onClick={() => reserve(bed)}
                            className="whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {busy === bed.bed_id
                              ? "Holding…"
                              : activeReservation &&
                                  ["ALLOCATED", "OCCUPIED"].includes(
                                    activeReservation.status,
                                  )
                                ? "View only"
                                : activeReservation
                                  ? "Reservation active"
                                  : !bookingWindow.can_book
                                    ? "Booking closed"
                                    : "Select bed"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
        {requestView && activeReservation && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-900">
            <span className="font-semibold">Current allocation:</span>{" "}
            {activeReservation.block_name} · Room {activeReservation.room_number} ·
            Bed {activeReservation.bed_number}
          </div>
        )}
        {requestView && (
          <section className="space-y-4">
            {excessCredits.length > 0 && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">
                        Accommodation credits
                      </p>
                      <p className="truncate text-xs text-slate-600">
                        Available credit from an approved room change.
                      </p>
                    </div>
                  </div>
                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    {excessCredits.filter(
                      (credit) =>
                        credit.status === "AVAILABLE" &&
                        ["APPROVED", "RESOLVED"].includes(
                          credit.request_status,
                        ),
                    ).length} available
                  </span>
                </div>
                <div className="mt-2 divide-y divide-emerald-200/70">
                  {excessCredits.map((credit) => (
                    <div
                      key={credit.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {money(credit.remaining_amount)} available
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {credit.request_number || "Room-change credit"} ·{" "}
                          {formatDate(credit.created_at)} · Paid{" "}
                          {money(credit.original_paid_amount)}
                        </p>
                      </div>
                      {credit.status === "AVAILABLE" &&
                        ["APPROVED", "RESOLVED"].includes(
                          credit.request_status,
                        ) && (
                          <button
                            type="button"
                            disabled={busy === `credit-claim-${credit.id}`}
                            onClick={() => requestCreditClaim(credit)}
                            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-800 disabled:opacity-50"
                          >
                            {busy === `credit-claim-${credit.id}`
                              ? "Submitting…"
                              : "Request refund"}
                          </button>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid gap-4 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
              <div className="rounded-lg border bg-white p-4">
                <div className="flex items-center gap-2 border-b pb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">
                      New request
                    </h2>
                    <p className="text-xs text-slate-500">
                      Ask the hostel office for help.
                    </p>
                  </div>
                </div>
                <form onSubmit={submitRequest} className="mt-3 space-y-2.5">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Type
                      </span>
                      <select
                        required
                        value={requestForm.requestType}
                        onChange={(event) =>
                          setRequestForm((current) => ({
                            ...current,
                            requestType: event.target.value,
                            targetBedId:
                              event.target.value === "room_change"
                                ? current.targetBedId
                                : "",
                          }))
                        }
                        className="mt-1 w-full rounded-md border bg-white px-2.5 py-2 text-sm"
                      >
                        <option value="maintenance">Maintenance / repair</option>
                        <option value="room_change">Room change</option>
                        <option value="checkout">Check-out request</option>
                        <option value="key_replacement">Key replacement</option>
                        <option value="welfare">Welfare concern</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Priority
                      </span>
                      <select
                        value={requestForm.priority}
                        onChange={(event) =>
                          setRequestForm({
                            ...requestForm,
                            priority: event.target.value,
                          })
                        }
                        className="mt-1 w-full rounded-md border bg-white px-2.5 py-2 text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </label>
                  </div>
                  {requestForm.requestType === "room_change" && (
                    <div className="rounded-md border border-blue-100 bg-blue-50/60 p-2.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-800">
                        Transfer to an available bed
                      </p>
                      {activeReservation ? (
                        <>
                          <p className="mt-1 text-xs text-slate-600">
                            Current: {activeReservation.block_name} · Room{" "}
                            {activeReservation.room_number} · Bed{" "}
                            {activeReservation.bed_number}
                          </p>
                          <select
                            required
                            value={requestForm.targetBedId}
                            onChange={(event) =>
                              setRequestForm((current) => ({
                                ...current,
                                targetBedId: event.target.value,
                              }))
                            }
                            className="mt-2 w-full rounded-md border bg-white px-2.5 py-2 text-sm"
                          >
                            <option value="">
                              {roomChangeLoading
                                ? "Loading available beds…"
                                : "Select a room and bed"}
                            </option>
                            {roomChangeBeds.map((bed) => (
                              <option key={bed.bed_id} value={bed.bed_id}>
                                {bed.block_name} · Room {bed.room_number} · Bed{" "}
                                {bed.bed_number} — {money(bed.amount)}
                              </option>
                            ))}
                          </select>
                          {selectedRoomChangeBed && (
                            <div className="mt-2 grid gap-1 text-[11px] text-slate-600 sm:grid-cols-3">
                              <span>
                                Paid: <strong>{money(currentPaidAmount)}</strong>
                              </span>
                              <span>
                                New: <strong>{money(selectedRoomChangeBed.amount)}</strong>
                              </span>
                              <span>
                                Extra: <strong className="text-amber-700">{money(roomChangeDifference)}</strong>
                              </span>
                            </div>
                          )}
                          {!roomChangeLoading && !roomChangeBeds.length && (
                            <p className="mt-2 text-xs text-amber-800">
                              No eligible available beds were found.
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="mt-1 text-xs text-amber-800">
                          You need an allocated bed before requesting a room change.
                        </p>
                      )}
                    </div>
                  )}
                  <input
                    required
                    minLength="3"
                    placeholder="Short subject"
                    value={requestForm.subject}
                    onChange={(event) =>
                      setRequestForm({
                        ...requestForm,
                        subject: event.target.value,
                      })
                    }
                    className="w-full rounded-md border px-2.5 py-2 text-sm"
                  />
                  <textarea
                    required
                    minLength="10"
                    rows="3"
                    placeholder="Describe the issue or request"
                    value={requestForm.description}
                    onChange={(event) =>
                      setRequestForm({
                        ...requestForm,
                        description: event.target.value,
                      })
                    }
                    className="w-full resize-y rounded-md border px-2.5 py-2 text-sm"
                  />
                  <button
                    disabled={busy === "request"}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    {busy === "request" ? "Submitting…" : "Submit request"}
                  </button>
                </form>
              </div>
              <div className="rounded-lg border bg-white p-4">
                <div className="flex items-center justify-between border-b pb-3">
                  <div className="flex items-center gap-2">
                    <div>
                      <h2 className="text-sm font-bold text-slate-900">
                        My requests
                      </h2>
                      <p className="text-xs text-slate-500">
                        Updates from the hostel office.
                      </p>
                    </div>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                    {requests.length}
                  </span>
                </div>
                {requests.length ? (
                  <div className="divide-y">
                    {requests.map((request) => (
                      <article key={request.id} className="py-3 first:pt-0 last:pb-0">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {request.subject}
                            </p>
                            <p className="mt-0.5 text-[11px] capitalize text-slate-500">
                              {request.request_type.replaceAll("_", " ")} ·{" "}
                              {formatDate(request.created_at)}
                            </p>
                          </div>
                          <span
                            className={`rounded-full border px-2 py-1 text-[10px] font-semibold ${request.status === "RESOLVED" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : request.status === "REJECTED" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
                          >
                            {request.status.replaceAll("_", " ")}
                          </span>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-slate-600">
                          {request.description}
                        </p>
                        {request.request_type === "room_change" && (
                          <div className="mt-2 border-l-2 border-blue-200 pl-2.5 text-[11px] text-slate-600">
                            <p className="font-semibold text-blue-900">
                              {request.block_name} · Room {request.room_number} · Bed {request.bed_number} → {request.target_block_name} · Room {request.target_room_number} · Bed {request.target_bed_number}
                            </p>
                            <p className="mt-0.5">
                              Difference: {money(request.difference_amount)} ·{" "}
                              {request.payment_status === "SETTLED"
                                ? "Paid"
                                : request.payment_status === "NOT_REQUIRED"
                                  ? "No payment required"
                                  : "Payment required before approval"}
                            </p>
                            {request.status === "AWAITING_PAYMENT" &&
                              request.room_change_invoice?.status ===
                                "PENDING_PAYMENT" && (
                                <button
                                  type="button"
                                  disabled={
                                    busy === `room-change-payment-${request.id}`
                                  }
                                  onClick={() =>
                                    confirmRoomChangePayment(request)
                                  }
                                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-primary-foreground disabled:opacity-50"
                                >
                                  {busy === `room-change-payment-${request.id}`
                                    ? "Processing…"
                                    : `Pay ${money(request.difference_amount)}`}
                                </button>
                              )}
                          </div>
                        )}
                        {request.staff_note && (
                          <p className="mt-2 rounded-md bg-slate-50 px-2.5 py-2 text-[11px] text-slate-600">
                            <strong>Staff note:</strong> {request.staff_note}
                          </p>
                        )}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="grid min-h-28 place-items-center text-center text-xs text-slate-500">
                    No accommodation requests yet.
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </section>
    </StudentLayout>
  );
};

export default HostelReservationPortal;
